/**
 * SpeedCurve Metronome - IndexedDB Database Layer
 * Provides CRUD operations for all user data with async IndexedDB API
 *
 * REFACTORED: All operations use inline transaction pattern to ensure
 * transactions stay alive during async operations (avoids GC race conditions)
 */

const DB_NAME = 'speedcurve_metronome';
const DB_VERSION = 1;

const STORES = {
  SESSIONS: 'sessions',
  STATS: 'stats',
  ACHIEVEMENTS: 'achievements',
  PRESETS: 'presets'
};

class Database {
  constructor() {
    this.db = null;
  }

  async open() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Sessions store - indexed by timestamp
        if (!db.objectStoreNames.contains(STORES.SESSIONS)) {
          const sessionsStore = db.createObjectStore(STORES.SESSIONS, { keyPath: 'id' });
          sessionsStore.createIndex('timestamp', 'timestamp', { unique: false });
          sessionsStore.createIndex('program', 'program', { unique: false });
        }

        // Stats store - single record for aggregated stats
        if (!db.objectStoreNames.contains(STORES.STATS)) {
          db.createObjectStore(STORES.STATS, { keyPath: 'id' });
        }

        // Achievements store - indexed by id
        if (!db.objectStoreNames.contains(STORES.ACHIEVEMENTS)) {
          db.createObjectStore(STORES.ACHIEVEMENTS, { keyPath: 'id' });
        }

        // Presets store - indexed by name
        if (!db.objectStoreNames.contains(STORES.PRESETS)) {
          db.createObjectStore(STORES.PRESETS, { keyPath: 'name' });
        }
      };
    });
  }

  // ============ SESSIONS ============

  async addSession(session) {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.SESSIONS, 'readwrite');
      const store = transaction.objectStore(STORES.SESSIONS);
      const request = store.add(session);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updateSession(session) {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.SESSIONS, 'readwrite');
      const store = transaction.objectStore(STORES.SESSIONS);
      const request = store.put(session);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getSession(id) {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.SESSIONS, 'readonly');
      const store = transaction.objectStore(STORES.SESSIONS);
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllSessions() {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.SESSIONS, 'readonly');
      const store = transaction.objectStore(STORES.SESSIONS);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async getSessionsByDateRange(startDate, endDate) {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.SESSIONS, 'readonly');
      const store = transaction.objectStore(STORES.SESSIONS);
      const index = store.index('timestamp');
      const request = index.getAll(IDBKeyRange.bound(startDate, endDate));
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteSession(id) {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.SESSIONS, 'readwrite');
      const store = transaction.objectStore(STORES.SESSIONS);
      const request = store.delete(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clearSessions() {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.SESSIONS, 'readwrite');
      const store = transaction.objectStore(STORES.SESSIONS);
      const request = store.clear();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ============ STATS ============

  async saveStats(stats) {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.STATS, 'readwrite');
      const store = transaction.objectStore(STORES.STATS);
      const request = store.put({ id: 'user_stats', ...stats });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getStats() {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.STATS, 'readonly');
      const store = transaction.objectStore(STORES.STATS);
      const request = store.get('user_stats');
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  // ============ ACHIEVEMENTS ============

  async saveAchievement(achievement) {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    if (!achievement.id) throw new Error('Achievement must have an id');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.ACHIEVEMENTS, 'readwrite');
      const store = transaction.objectStore(STORES.ACHIEVEMENTS);
      const request = store.put({ id: achievement.id, ...achievement });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAchievement(id) {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.ACHIEVEMENTS, 'readonly');
      const store = transaction.objectStore(STORES.ACHIEVEMENTS);
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllAchievements() {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.ACHIEVEMENTS, 'readonly');
      const store = transaction.objectStore(STORES.ACHIEVEMENTS);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async clearAchievements() {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.ACHIEVEMENTS, 'readwrite');
      const store = transaction.objectStore(STORES.ACHIEVEMENTS);
      const request = store.clear();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ============ PRESETS ============

  async savePreset(name, settings) {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    if (!name) throw new Error('Preset name is required');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.PRESETS, 'readwrite');
      const store = transaction.objectStore(STORES.PRESETS);
      const request = store.put({ name, settings, updatedAt: new Date().toISOString() });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPreset(name) {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.PRESETS, 'readonly');
      const store = transaction.objectStore(STORES.PRESETS);
      const request = store.get(name);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllPresets() {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.PRESETS, 'readonly');
      const store = transaction.objectStore(STORES.PRESETS);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async deletePreset(name) {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.PRESETS, 'readwrite');
      const store = transaction.objectStore(STORES.PRESETS);
      const request = store.delete(name);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clearPresets() {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.PRESETS, 'readwrite');
      const store = transaction.objectStore(STORES.PRESETS);
      const request = store.clear();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ============ EXPORT/IMPORT ============

  async exportAll() {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    const [sessions, stats, achievements, presets] = await Promise.all([
      this.getAllSessions(),
      this.getStats(),
      this.getAllAchievements(),
      this.getAllPresets()
    ]);

    // Get current app settings from localStorage (safely)
    let settings = null;
    try {
      const autosave = localStorage.getItem('speedcurve_autosave');
      settings = autosave ? JSON.parse(autosave) : null;
    } catch (e) {
      console.warn('Failed to parse autosave:', e);
    }

    return {
      version: DB_VERSION,
      exportedAt: new Date().toISOString(),
      data: {
        sessions,
        stats: stats || {},
        achievements,
        presets: presets.map(p => ({ name: p.name, settings: p.settings }))
      }
    };
  }

  async importAll(exportData, mode = 'merge') {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    if (!exportData || !exportData.data) {
      throw new Error('Invalid import file format');
    }

    const { data } = exportData;

    if (mode === 'replace') {
      // Clear all stores in sequence (atomic enough for this use case)
      try {
        await this.clearSessions();
        await this.clearAchievements();
        await this.clearPresets();
      } catch (e) {
        console.error('Failed to clear stores during import:', e);
        throw e;
      }
    }

    // Import sessions
    try {
      if (data.sessions && Array.isArray(data.sessions)) {
        for (const session of data.sessions) {
          await this.updateSession(session);
        }
      }
    } catch (e) {
      console.error('Failed to import sessions:', e);
    }

    // Import stats
    try {
      if (data.stats && Object.keys(data.stats).length > 0) {
        await this.saveStats(data.stats);
      }
    } catch (e) {
      console.error('Failed to import stats:', e);
    }

    // Import achievements
    try {
      if (data.achievements && Array.isArray(data.achievements)) {
        for (const achievement of data.achievements) {
          await this.saveAchievement(achievement);
        }
      }
    } catch (e) {
      console.error('Failed to import achievements:', e);
    }

    // Import presets
    try {
      if (data.presets && Array.isArray(data.presets)) {
        for (const preset of data.presets) {
          if (preset.name && preset.settings) {
            await this.savePreset(preset.name, preset.settings);
          }
        }
      }
    } catch (e) {
      console.error('Failed to import presets:', e);
    }

    // Import settings (auto-save state)
    try {
      if (data.settings) {
        localStorage.setItem('speedcurve_autosave', JSON.stringify(data.settings));
      }
    } catch (e) {
      console.error('Failed to import settings:', e);
    }
  }

  async clearAll() {
    if (!this.db) throw new Error('Database not initialized. Call open() first.');
    // Clear in sequence to avoid transaction conflicts
    await this.clearSessions();
    await this.clearAchievements();
    await this.clearPresets();
    // Clear stats (single record)
    await new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORES.STATS, 'readwrite');
      const store = transaction.objectStore(STORES.STATS);
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

// Singleton instance
const db = new Database();

export { db, STORES };
export default db;